//eslint-disable-next-line
/*global google*/
import React from "react";
import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { TouchBackend } from "react-dnd-touch-backend";
import TagManager from "react-gtm-module";

import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { StyledEngineProvider, ThemeProvider } from "@mui/material";

import theme from "@aclymatepackages/themes";

import "@fontsource/poppins";
import "@fontsource/open-sans";
import App from "./App";
import * as serviceWorker from "./serviceWorker";
import { fetchOurApi } from "./helpers/utils/apiCalls";
import { startTestingRecorder } from "./helpers/testing-recorder";
import {
  sentryBeforeSend,
  sentryBeforeBreadcrumb,
  shouldRecordReplay,
  REPLAY_UNMASKED_SELECTORS
} from "./helpers/sentryHelpers";

TagManager.initialize({ gtmId: "GTM-PNHV7VR" });
startTestingRecorder();

// Checked once at init rather than per navigation: the excluded routes are
// entry-point flows reached from emailed links, so a session that starts on
// one stays on it.
const buildSentryIntegrations = () => {
  if (!shouldRecordReplay(window.location.pathname)) {
    return [];
  }

  return [
    Sentry.replayIntegration({
      maskAllText: true,
      blockAllMedia: false,
      unmask: REPLAY_UNMASKED_SELECTORS
    })
  ];
};

const initiateSentry = () => {
  if (process.env.NODE_ENV !== "development") {
    Sentry.init({
      dsn: "https://2ba6804ae40e48f38e6efb3f3b7e00f8@o1400981.ingest.sentry.io/6731175",
      environment: process.env.REACT_APP_ENVIRONMENT,
      integrations: buildSentryIntegrations(),
      tracesSampleRate: 1.0,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      beforeSend: sentryBeforeSend,
      beforeBreadcrumb: sentryBeforeBreadcrumb
    });
  }
};

initiateSentry();

const URL_FEATURE_MAP = {
  dashboard: "Dashboard",
  settings: "Settings",
  reporting: "Reporting",
  accounting: "Accounting",
  purchase: "Carbon Offsets",
  badges: "Certifications",
  simulation: "Simulation",
  events: "Events",
  products: "Products"
};

const getFeatureFromUrl = (url) => {
  try {
    const segments = new URL(url).pathname.split("/").filter(Boolean);
    return URL_FEATURE_MAP[segments[2]] || "Unknown Feature";
  } catch {
    return "Unknown Feature";
  }
};

const registerUnhandledRejectionReporter = () => {
  if (process.env.NODE_ENV === "development") {
    return;
  }
  window.addEventListener("unhandledrejection", (event) => {
    const { reason } = event;
    const message = reason?.message || String(reason);
    const stack = reason?.stack || null;
    fetchOurApi({
      path: "/errors/report",
      method: "POST",
      data: {
        featureName: getFeatureFromUrl(window.location.href),
        message,
        stack,
        url: window.location.href
      }
    }).catch(() => {});
  });
};

registerUnhandledRejectionReporter();

const dndBackend = "ontouchstart" in window ? TouchBackend : HTML5Backend;

const container = document.getElementById("root");
const root = createRoot(container);

root.render(
  <React.Fragment>
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <DndProvider backend={dndBackend}>
            <App />
          </DndProvider>
        </ThemeProvider>
      </StyledEngineProvider>
    </LocalizationProvider>
  </React.Fragment>
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
